<script setup lang="ts">
import type { IconName } from './types'
import SkeletonCircle from '@ui/components/skeleton/SkeletonCircle.vue'
import { useAttrs } from 'vue'

defineOptions({ inheritAttrs: false })

withDefaults(
  defineProps<{
    name?: IconName
    loading?: boolean
    /** Defer loading an image icon until it scrolls into view, e.g. in long lists. */
    lazy?: boolean
  }>(),
  {
    loading: false,
    lazy: false,
  },
)

const attrs = useAttrs()

// Icons are decorative by default; an accessible name turns them into an image.
// Their size comes from CSS, so `width`/`height` attributes become inline styles.
function iconAttrs() {
  const { width, height, style, ...rest } = attrs
  const labelled = rest['aria-label'] != null || rest['aria-labelledby'] != null
  // Only sizes that were passed, so `style` keeps its own width/height otherwise.
  const size = Object.fromEntries(
    Object.entries({ width: dimension(width), height: dimension(height) }).filter(([, value]) => value),
  )
  const sized = Object.keys(size).length > 0
  // An explicit `style: undefined` still renders `style=""` during SSR.
  return {
    ...(labelled && { 'aria-hidden': undefined, 'role': 'img' }),
    ...rest,
    ...((sized || style) && { style: sized ? [style, size] : style }),
  }
}

function imageAlt() {
  return String(attrs.alt ?? attrs['aria-label'] ?? '')
}

function dimension(value: unknown) {
  if (value == null) {
    return undefined
  }

  const normalized = String(value).trim()
  if (!normalized) {
    return undefined
  }

  return /^\d+(?:\.\d+)?$/.test(normalized) ? `${normalized}px` : normalized
}
</script>

<template lang="pug">
span.ui-icon(v-if="loading" v-bind="iconAttrs()")
  SkeletonCircle
img.ui-icon(v-else-if="name && typeof name === 'string'" :src="name" :alt="imageAlt()" :loading="lazy ? 'lazy' : undefined" decoding="async" v-bind="iconAttrs()")
component(:is="name" v-else-if="name" v-bind="iconAttrs()")
</template>

<style scoped>
img.ui-icon {
  object-fit: contain;
}

.ui-icon > .skeleton-circle {
  --uno: d-block s-100p;
}
</style>
